import { gql, useLazyQuery } from '@apollo/client';
import IconOverflow from '@apollo/icons/default/IconOverflow.svg';
import {
  IconButton,
  Menu,
  MenuButton,
  MenuContent,
  MenuItem,
  MenuList,
} from '@apollo/orbit';
import { saveAs } from 'file-saver';
import React, { useCallback, useState } from 'react';

import { PermissionGuard } from 'src/components/permissionGuards/permissionGuard/PermissionGuard';
import { useShowToast } from 'src/components/toast/Toast';
import {
  PersistedQueryListActionsMenuDownloadQuery,
  PersistedQueryListActionsMenuDownloadQueryVariables,
  PersistedQueryListDetailsFragment,
} from 'src/lib/graphqlTypes/types';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';

import { PersistedQueryListDeleteModal } from './persistedQueryListDeleteModal/PersistedQueryListDeleteModal';
import { PersistedQueryListLinkUpdateModal } from './persistedQueryListLinkUpdateModal/PersistedQueryListLinkUpdateModal';
import { PersistedQueryListMetaUpdateModal } from './persistedQueryListMetaUpdateModal/PersistedQueryListMetaUpdateModal';
import { PersistedQueryListRepublishModal } from './persistedQueryListRepublishModal/PersistedQueryListRepublishModal';

interface Props {
  graphId: string;
  list: PersistedQueryListDetailsFragment;
}

const persistedQueryListActionsMenuDownloadQuery = gql<
  PersistedQueryListActionsMenuDownloadQuery,
  PersistedQueryListActionsMenuDownloadQueryVariables
>`
  query PersistedQueryListActionsMenuDownloadQuery(
    $graphId: ID!
    $listId: ID!
  ) {
    graph(id: $graphId) {
      id
      persistedQueryList(id: $listId) {
        id
        currentBuild {
          id
          manifestChunks {
            id
            json
          }
        }
      }
    }
  }
`;

type OpenModalState = 'PUBLISH' | 'META' | 'LINKS' | 'DELETE';

type PersistedQueryManifest = {
  format: string;
  version: number;
  operations: string[];
};

export const PersistedQueryListActionsMenu = ({ graphId, list }: Props) => {
  const [fetchManifests, { loading }] = useLazyQuery(
    persistedQueryListActionsMenuDownloadQuery,
    {
      variables: {
        graphId,
        listId: list.id,
      },
      fetchPolicy: 'network-only',
    },
  );
  const { showToasts } = useShowToast();

  const [openModalState, setOpenModalState] = useState<OpenModalState | null>(
    null,
  );

  const fetchAndSaveAs = useCallback(async () => {
    const { data } = await fetchManifests();
    const manifestChunks =
      data?.graph?.persistedQueryList?.currentBuild.manifestChunks;
    if (manifestChunks && isMinLengthArray(0, manifestChunks)) {
      const stringChunks = [
        '{"format":"apollo-persisted-query-manifest","version":1,"operations":[',
      ];
      manifestChunks
        .map((chunk) => JSON.parse(chunk.json) as PersistedQueryManifest)
        .flatMap(({ operations }) => operations)
        .forEach((operation) => {
          if (stringChunks.length > 1) {
            stringChunks.push(',');
          }
          stringChunks.push(JSON.stringify(operation));
        });
      stringChunks.push(']}');
      saveAs(
        new Blob(stringChunks, { type: 'text/json;charset=utf-8' }),
        `persisted-query-manifest-${graphId}-${list.id}.json`,
      );
    } else if (manifestChunks) {
      // show a toast with no operations - this should be impossible since we
      // hide the download button when 0 ops, but just in case.
      showToasts({
        heading: 'No operations in manifest',
        message:
          "Downloads will be available once you've published operations to the manifest.",
        level: 'info',
      });
    } else {
      // show a toast with the error
      showToasts({
        heading: 'Download failed',
        message: 'Something went wrong!  Please try again later.',
        level: 'error',
      });
    }
  }, [fetchManifests, graphId, list, showToasts]);

  return (
    <>
      <Menu placement="bottom-end" autoSelect={false}>
        <MenuButton
          as={IconButton}
          variant="secondary"
          aria-label="Show all actions"
          isLoading={loading}
        >
          <IconOverflow />
        </MenuButton>
        <MenuContent>
          <MenuList>
            <MenuItem
              onClick={fetchAndSaveAs}
              data-analytics-category="Persisted Queries"
              data-analytics-label="action menu download"
              /* would ideally be using 'disabled' here but there is a Chakra bug:
            https://github.com/chakra-ui/chakra-ui/issues/7269 */
              isDisabled={list.currentBuild.totalOperationsInList === 0}
            >
              Download as JSON
            </MenuItem>
            <PermissionGuard
              graphId={graphId}
              graphPermissions="canManagePersistedQueryLists"
              fallback={null}
            >
              <MenuItem
                onClick={() => setOpenModalState('PUBLISH')}
                data-analytics-category="Persisted Queries"
                data-analytics-label="action menu publish"
              >
                Publish Operations
              </MenuItem>
              <MenuItem
                onClick={() => setOpenModalState('META')}
                data-analytics-category="Persisted Queries"
                data-analytics-label="action menu metadata"
              >
                Update Name and Description
              </MenuItem>
              <MenuItem
                onClick={() => setOpenModalState('LINKS')}
                data-analytics-category="Persisted Queries"
                data-analytics-label="action menu link"
              >
                Link and Unlink Variants
              </MenuItem>
              <MenuItem
                className="text-error hover:bg-error hover:text-error"
                onClick={() => setOpenModalState('DELETE')}
                data-analytics-category="Persisted Queries"
                data-analytics-label="action menu delete"
              >
                Delete
              </MenuItem>
            </PermissionGuard>
          </MenuList>
        </MenuContent>
      </Menu>
      <PersistedQueryListRepublishModal
        isOpen={openModalState === 'PUBLISH'}
        graphId={graphId}
        listId={list.id}
        onClose={() => setOpenModalState(null)}
      />
      <PersistedQueryListMetaUpdateModal
        isOpen={openModalState === 'META'}
        graphId={graphId}
        listId={list.id}
        onClose={() => setOpenModalState(null)}
      />
      <PersistedQueryListLinkUpdateModal
        graphId={graphId}
        listId={list.id}
        onClose={() => setOpenModalState(null)}
        isOpen={openModalState === 'LINKS'}
      />
      <PersistedQueryListDeleteModal
        isOpen={openModalState === 'DELETE'}
        graphId={graphId}
        listId={list.id}
        onClose={() => setOpenModalState(null)}
        onRedirectToUnlink={() => {
          setOpenModalState('LINKS');
        }}
      />
    </>
  );
};
